import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import IconButton from "../LogoButton/IconButton";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { enviroment } from "../../../enviroment";
import {
  hideOversizeSheetInlineRequest,
  markSheetSizeRequestedInline,
  openOversizeSheetDialog,
  useOversizeSheetGuard,
  useOversizeSheetRequested,
} from "../../../hooks/useOversizeSheetGuard";
import { useSheetSizeRequestSender } from "../../../hooks/useSheetSizeRequestSender";
import {
  collectOversizeFromCartLogoLines,
  collectOversizeFromLogos,
  DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS,
  formatCm,
  OVERSIZE_SHEET_MAX_REQUESTS,
  OversizeLogoInfo,
} from "../../../utils/functions/oversizeSheet";

// Oversize sheet guard, inline request (flag
// REACT_APP_OVERSIZE_SHEET_INLINE_REQUEST_ENABLED, Nichlas 28/9: "der må
// ikke være knappen men i stedet det med forespørg ny design"): where a
// single logo (logo list row, logo details) would show the quantity
// stepper + "Add to order", an oversize image logo shows "Request new
// sheet size" instead - the SAME
// request as the dialog (useSheetSizeRequestSender), only on click, with
// "Request sent" from the same per-viewer sessionStorage memory (TTL from
// the hint) and no button at all when the hint says requestEnabled=false
// (the explanation stays, so the row is never silently empty). The dialog
// and the API's 409 at commit/place remain the safety net (bulk add,
// reorder, old tabs, other paths).
//
// LOGOS ONLY (Nichlas 28/9: "Design skal ikke påvirkes af dette" / "de er
// udelukkende logoer"): a design - and a logo placed in a design - never
// gets the inline request; the design list and design details keep
// today's stepper + "Add to order".

export interface OversizeSheetInlineView {
  /** The oversize image logo(s) of the one logo. */
  logos: OversizeLogoInfo[];
  /** Hours a sent request is remembered (the hint's requestTtlHours). */
  ttlHours: number;
  /** The hint's requestEnabled: false = no request button. */
  requestEnabled: boolean;
}

/**
 * The inline view for one standalone logo, or null = render today's
 * stepper + "Add to order". Non-null ONLY with both flags on, the guard
 * ACTIVE for the session (own chain listed, or an admin acting as a listed
 * vendor) and the logo an image logo over the hint's limit (maxMm +
 * bothSides). Designs are never judged here. 0 new calls: the hint is the
 * cached logos/options/all (flag off = a disabled observer).
 */
export function useOversizeSheetInline(source: {
  logo?: BaseLogo | null;
}): OversizeSheetInlineView | null {
  const flag =
    enviroment.oversizeSheetGuardEnabled && enviroment.oversizeSheetInlineRequestEnabled;
  const guard = useOversizeSheetGuard({ enabled: flag });
  const { logo } = source;
  const { active, maxMm, bothSides } = guard;
  const requestEnabled = guard.hint.requestEnabled === true;
  const ttlHours =
    guard.hint.requestTtlHours > 0
      ? guard.hint.requestTtlHours
      : DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS;
  return useMemo(() => {
    if (!flag || !active || !logo) return null;
    const logos = collectOversizeFromLogos([logo], maxMm, bothSides);
    if (logos.length === 0) return null;
    return { logos, ttlHours, requestEnabled };
  }, [flag, active, logo, maxMm, bothSides, ttlHours, requestEnabled]);
}

const NO_OFFENDING: OversizeLogoInfo[] = [];

export interface OversizeCartCheckoutGate {
  /** The oversize image logos of the cart's standalone logo lines ([] = none / inert). */
  offending: OversizeLogoInfo[];
  /**
   * Checkout start ("Continue" on the cart): true = stopped - the dialog
   * (context "place": remove them or request a new sheet size) is open and
   * the caller must NOT go on to the draft / checkout. false = go on as
   * today (flags off, guard inactive, no offending line).
   */
  stopAtCheckout: () => boolean;
  /**
   * The heating-fee dialog's extra copies (Continue after the dialog): an
   * extra copy with quantity > 0 becomes a NEW standalone logo line
   * (applyExtraLogos), which the API judges as a loose logo (409
   * sheetSizeUnavailable) - also when it is a copy of a design's own
   * transfer logo. true = stopped (the dialog opens with context "add":
   * the logo was not added) and the caller must NOT build the draft. The
   * design itself is never judged - without the extra copy it goes on.
   */
  stopAtExtraLogos: (
    extraLogos: readonly ({ logo?: BaseLogo | null; quantity?: number } | null | undefined)[]
  ) => boolean;
}

/**
 * The cart hole (Nichlas 28/9, same flag as the inline request): a
 * standalone oversize logo line can already sit in the cart - restored
 * from the per-identity localStorage bucket, added while the guard did not
 * apply (before it went live, another act-as org) or kept through a
 * quantity change (handleLogoSetQuantities -> updateInCart is not an add).
 * Those lines are judged on the cart items the page already holds - 0 new
 * calls - and the checkout start opens the dialog instead of going on.
 * Design lines are never judged. The API's 409 at commit/place stays the
 * backstop.
 */
export function useOversizeCartCheckoutGate(
  items: readonly ({ type?: unknown; baseLogo?: BaseLogo | null } | null | undefined)[]
): OversizeCartCheckoutGate {
  const flag =
    enviroment.oversizeSheetGuardEnabled && enviroment.oversizeSheetInlineRequestEnabled;
  const guard = useOversizeSheetGuard({ enabled: flag });
  const { active, maxMm, bothSides, dialogOptions } = guard;
  const offending = useMemo(
    () =>
      flag && active ? collectOversizeFromCartLogoLines(items, maxMm, bothSides) : NO_OFFENDING,
    [flag, active, items, maxMm, bothSides]
  );
  return {
    offending,
    stopAtCheckout: () => {
      if (!flag || !active || offending.length === 0) return false;
      openOversizeSheetDialog(offending, "place", dialogOptions);
      return true;
    },
    stopAtExtraLogos: (extraLogos) => {
      if (!flag || !active) return false;
      const extras = collectOversizeFromLogos(
        (extraLogos ?? []).map((extra) =>
          extra && (extra.quantity ?? 0) > 0 ? extra.logo : null
        ),
        maxMm,
        bothSides
      );
      if (extras.length === 0) return false;
      openOversizeSheetDialog(extras, "add", dialogOptions);
      return true;
    },
  };
}

/**
 * "Request new sheet size" in place of the stepper + "Add to order".
 * `layout` "row" = the list row's compact button (explanation + sizes in
 * the tooltip); "block" = full width with the explanation below it (logo
 * details).
 */
export function OversizeSheetInlineRequest({
  view,
  layout = "row",
}: {
  view: OversizeSheetInlineView;
  layout?: "row" | "block";
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || undefined;
  const sendRequests = useSheetSizeRequestSender();
  const { requested, notConfigured } = useOversizeSheetRequested(view.ttlHours, true);
  const [requesting, setRequesting] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const allRequested =
    view.logos.length > 0 && view.logos.every((l) => requested[l.logoId] !== undefined);
  const showButton = view.requestEnabled && !notConfigured;
  const lines = view.logos.map((logo) =>
    t("oversizeSheet.logoLine", {
      title: logo.title,
      serial: logo.serial,
      widthCm: formatCm(logo.widthMm, locale),
      heightCm: formatCm(logo.heightMm, locale),
    })
  );
  const tooltip = [t("oversizeSheet.title"), ...lines].join("\n");

  const onRequest = async (e?: React.MouseEvent<HTMLElement>) => {
    // The row / card around the button opens the details on click.
    e?.stopPropagation();
    if (inFlight.current) return;
    const pending = view.logos
      .filter((l) => requested[l.logoId] === undefined)
      .slice(0, OVERSIZE_SHEET_MAX_REQUESTS);
    if (pending.length === 0) return;
    inFlight.current = true;
    setRequesting(true);
    // Not aborted on unmount: the click was the viewer's intent (a list
    // re-render must not cancel it); each call keeps its own 8 s abort.
    const loop = new AbortController();
    try {
      await sendRequests(pending, loop.signal, {
        onRequested: (logoId) => markSheetSizeRequestedInline(logoId, view.ttlHours),
        onNotConfigured: hideOversizeSheetInlineRequest,
      });
    } finally {
      inFlight.current = false;
      if (mounted.current) setRequesting(false);
    }
  };

  const button = showButton ? (
    <IconButton
      key={"oversize-sheet-request"}
      onClick={onRequest}
      disabled={allRequested || requesting}
      icon={<FontAwesomeIcon icon={faTriangleExclamation} className="text-[#E8A33D]" />}
      variant="success"
      width={layout === "block" ? "full" : undefined}
    >
      {allRequested ? t("oversizeSheet.requested") : t("oversizeSheet.request")}
    </IconButton>
  ) : null;

  if (layout === "block") {
    return (
      <div
        className="flex flex-col gap-y-1 w-full"
        data-testid="oversize-sheet-inline"
        title={tooltip}
      >
        {button}
        <span className="text-xs text-gray-600" data-testid="oversize-sheet-inline-hint">
          {t("oversizeSheet.title")}
        </span>
        {lines.map((line, i) => (
          <span key={view.logos[i].logoId} className="text-xs text-gray-500">
            {line}
          </span>
        ))}
      </div>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-x-2"
      data-testid="oversize-sheet-inline"
      title={tooltip}
    >
      {button ?? (
        <span
          className="inline-flex items-center gap-x-1 text-xs text-gray-600"
          data-testid="oversize-sheet-inline-hint"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-[#E8A33D]" />
          {t("oversizeSheet.title")}
        </span>
      )}
    </span>
  );
}
